'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { SearchApi } from '@ps/api/search';
import {
  AvatarMeListingVariantSkuEnumItem,
  type PurchasedAvatarMeMediaItem,
  SearchPurchasedAvatarMeMediaSortByEnumItem,
} from '@ps/api/types';
import { withClientAPI } from '@ps/functions/utils/api';

/**
 * Fetches the AI-generated media for a `wearWithAiListingId` via the same
 * endpoint legacy uses: `SearchApi.searchApiControllerSearchPurchasedAvatarMeMediaFromMe`
 * with the `wearWithAiListingId` filter and the studio variant SKUs.
 *
 * Pagination is collapsed to a single 100-row page (matches the legacy
 * overlay call site, which sets `pageSize: 100`). The page covers the PDP
 * gallery and the carousel — no infinite-scroll needed here.
 *
 * `refresh` re-runs the fetch; the overlay calls it after a favorite /
 * visibility toggle so the cached row reflects the new state.
 */
export interface UsePurchasedAvatarMeListingMediaParams {
  sellerId: string | undefined;
  listingId: string | undefined;
  pageSize?: number;
  /** Override the variant filter (defaults to the studio media variants). */
  variantSku?: AvatarMeListingVariantSkuEnumItem[];
  /** Skip the fetch entirely (e.g. when not authed). */
  enabled?: boolean;
}

const kDefaultVariants: AvatarMeListingVariantSkuEnumItem[] = [
  AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMeStudioImage,
  AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMeStudioVideo5s,
  AvatarMeListingVariantSkuEnumItem.ComPopstoreAvatarMeStudioVideo10s,
];

export function usePurchasedAvatarMeListingMedia({
  sellerId,
  listingId,
  pageSize = 100,
  variantSku = kDefaultVariants,
  enabled = true,
}: UsePurchasedAvatarMeListingMediaParams): {
  rows: PurchasedAvatarMeMediaItem[];
  loading: boolean;
  error: unknown;
  refresh: () => Promise<void>;
} {
  const [rows, setRows] = useState<PurchasedAvatarMeMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const reqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled || !sellerId || !listingId) {
      setRows([]);
      return;
    }
    const token = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const api = withClientAPI(SearchApi);
      const res =
        await api.searchApiControllerSearchPurchasedAvatarMeMediaFromMe({
          storeId: sellerId,
          pageNo: 1,
          pageSize,
          variantSku: [...variantSku],
          // The codegen expects an array — legacy passes a single
          // id; wrap it to satisfy the new API shape (same backend
          // semantics: filter to media tied to this listing).
          wearWithAiListingId: [listingId],
          // Match legacy: sort by createdAt so the carousel shows
          // generations in the order they were created.
          sortBy: SearchPurchasedAvatarMeMediaSortByEnumItem.CreatedAt,
        });
      // Stale guard — drop if another refresh started after this one.
      if (token !== reqRef.current) return;
      setRows(res.data?.rows ?? []);
    } catch (err) {
      if (token === reqRef.current) {
        console.error('[usePurchasedAvatarMeListingMedia]', err);
        setError(err);
      }
    } finally {
      if (token === reqRef.current) setLoading(false);
    }
  }, [enabled, sellerId, listingId, pageSize, variantSku]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { rows, loading, error, refresh };
}
